import { WebsocketProvider } from "y-websocket";
import * as YJS from "yjs";
import { Doc, EditorSchema, isDocEmpty } from "@smartsuite/smartdoc";
import { useEffect, useMemo, useRef, useState } from "react";
import { prosemirrorJSONToYDoc, yDocToProsemirrorJSON } from "y-prosemirror";
import { toByteArray } from "base64-js";
import { baseColors } from "./colors";

import { SentryService } from "../../services/SentryService/SentryService";
import { toRenderableDoc } from "../../helpers/sanitizeDoc";

export type YjsConnectionStatus = "connected" | "disconnected" | "connecting";
export type YjsProviderMode = "record" | "document";

export interface UseYjsProviderResult {
  yDoc: YJS.Doc;
  yjsProvider: WebsocketProvider;
  connectionStatus: YjsConnectionStatus;
  receivedFirstUpdate: boolean;
  wasEverConnected: boolean;
  isHydrated: boolean;
}

export interface UseYjsProviderProps {
  enabled: boolean;
  connect: boolean;
  accountSlug: string;
  applicationId: string;
  entityId: string;
  fieldSlug: string | null;
  mode: YjsProviderMode;
  initialDoc?: Doc;
  initialYjsData: string | undefined;
  /** May return `undefined` before the schema for the current extension set exists. */
  getSchema: () => EditorSchema | undefined;
  memberName: string;
  env: string;
  accessToken: string;
  webSocketURL: string;
}

export function useYjsProvider({
  enabled,
  connect,
  accountSlug,
  applicationId,
  entityId,
  fieldSlug,
  mode,
  initialDoc,
  initialYjsData,
  getSchema,
  memberName,
  env,
  accessToken,
  webSocketURL,
}: UseYjsProviderProps): UseYjsProviderResult {
  const shouldReconnect = mode === "document" || !!initialYjsData;
  const hasInitialDoc = !!initialDoc && !isDocEmpty(initialDoc);
  const prevProviderRef = useRef<WebsocketProvider | null>(null);
  const [yjsProvider, yDoc] = useMemo(() => {
    prevProviderRef.current?.destroy();

    const [nextProvider, nextDoc] = createYJSProvider(
      initialYjsData,
      accountSlug,
      applicationId,
      entityId,
      fieldSlug,
      memberName,
      env,
      accessToken,
      webSocketURL,
      mode
    );

    prevProviderRef.current = nextProvider;
    return [nextProvider, nextDoc];
  }, [
    accessToken,
    accountSlug,
    applicationId,
    entityId,
    env,
    fieldSlug,
    initialYjsData,
    memberName,
    mode,
    webSocketURL,
  ]);
  const [connectionStatus, setConnectionStatus] = useState<YjsConnectionStatus>(
    !connect ? "disconnected" : "connecting"
  );
  const [receivedFirstUpdate, setReceivedFirstUpdate] = useState<boolean>(
    !!initialYjsData || !hasInitialDoc
  );
  const [wasEverConnected, setWasEverConnected] = useState<boolean>(false);
  const [isHydrated, setIsHydrated] = useState<boolean>(!enabled || !connect || !!initialYjsData);

  useEffect(() => {
    setConnectionStatus(!connect ? "disconnected" : "connecting");
    setReceivedFirstUpdate(!!initialYjsData || !hasInitialDoc);
    setWasEverConnected(false);
    setIsHydrated(!enabled || !connect || !!initialYjsData);
  }, [connect, enabled, hasInitialDoc, initialYjsData, yDoc, yjsProvider]);

  useEffect(() => {
    if (!enabled || !connect) {
      populateValue(initialDoc, initialYjsData, yDoc, getSchema());
      setIsHydrated(true);
      return;
    }

    if (initialYjsData) {
      // collab is enabled, populate with `yjsData` if it exists
      YJS.applyUpdate(yDoc, toByteArray(initialYjsData));
      setIsHydrated(true);
    } else if (isDocEmpty(yDocToProsemirrorJSON(yDoc))) {
      // in case `yjsData` doesn't exist, the yjs-server will emit the initial value later on after the connection is established
    }

    let connectAttempt = 1;
    let hasEverConnected = false;

    const handleUpdate = (): void => {
      setReceivedFirstUpdate(true);
      setIsHydrated(true);
    };

    const handleSync = (synced: boolean): void => {
      if (synced) {
        setIsHydrated(true);
      }
    };

    const handleStatus = (event: { status: YjsConnectionStatus }): void => {
      setConnectionStatus(event.status);

      if (event.status === "connected") {
        hasEverConnected = true;
        setWasEverConnected(true);
        connectAttempt = 1;
      }
    };

    const handleConnectionError = (): void => {
      if (hasEverConnected) {
        // in case of a connection error after we were connected before,
        // we should try to reconnect when the user is back online if the initial field value contains a collaborative state
        setConnectionStatus("disconnected");
        yjsProvider.shouldConnect = shouldReconnect;
        return;
      }

      if (connectAttempt < 3) {
        // if never connected before, try to reconnect 3 times
        connectAttempt++;
      } else {
        // otherwise, populate yDoc with local data and don't reconnect unless the initial value
        // contains a collaborative state, in this case we assume its safe to reconnect
        populateValue(initialDoc, initialYjsData, yDoc, getSchema());
        setIsHydrated(true);
        setConnectionStatus("disconnected");
        yjsProvider.shouldConnect = shouldReconnect;
      }
    };

    yDoc.on("update", handleUpdate);
    yjsProvider.on("sync", handleSync);
    yjsProvider.on("status", handleStatus);
    yjsProvider.on("connection-error", handleConnectionError);

    if (connect) {
      yjsProvider.connect();
    }

    return () => {
      yDoc.off("update", handleUpdate);
      yjsProvider.off("sync", handleSync);
      yjsProvider.off("status", handleStatus);
      yjsProvider.off("connection-error", handleConnectionError);
      yjsProvider?.destroy();
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, connect, getSchema, initialDoc, initialYjsData, shouldReconnect, yDoc, yjsProvider]);

  return {
    yDoc,
    yjsProvider,
    connectionStatus,
    receivedFirstUpdate,
    wasEverConnected,
    isHydrated,
  };
}

/** Populate a collaborative document yDoc
 * with the value from yjsData or from the doc itself depending on the yjsData being available or not. */
export function populateValue(
  doc: Doc | undefined,
  yjsData: string | undefined,
  yDoc: YJS.Doc,
  schema: EditorSchema | undefined
): void {
  // eslint-disable-next-line no-extra-boolean-cast
  if (!!yjsData) {
    YJS.applyUpdate(yDoc, toByteArray(yjsData));
    return;
  }

  if (!doc || isDocEmpty(doc) || !isDocEmpty(yDocToProsemirrorJSON(yDoc))) {
    return;
  }

  if (!schema) {
    // Seeding the yDoc with the offline value is a best effort: skipping it leaves the
    // editor usable, whereas throwing here takes the whole widget down via the error
    // boundary. Report it so a regression is still visible.
    SentryService.captureException({
      error: new Error("Cannot populate the collaborative value: the schema is not available"),
      level: "warning",
    });
    return;
  }

  // Same untrusted document the editor renders, so it needs the same reconciliation -
  // `prosemirrorJSONToYDoc` parses it with `Node.fromJSON` and throws on anything the
  // schema does not know. `SmartdocWidget` reports on the identical document, so stay
  // quiet here rather than reporting it twice.
  const { doc: renderableDoc } = toRenderableDoc(doc, schema);

  if (!renderableDoc || isDocEmpty(renderableDoc)) {
    return;
  }

  const offlineDoc = prosemirrorJSONToYDoc(schema, renderableDoc);
  YJS.applyUpdate(yDoc, YJS.encodeStateAsUpdate(offlineDoc));
}

const defaultWebSocketURL = "wss://smartdoc.app.smartsuite.com";

function createYJSProvider(
  initialYjsData: string | undefined,
  accountSlug: string,
  applicationId: string,
  entityId: string,
  fieldSlug: string | null,
  memberName: string,
  env: string,
  accessToken: string,
  webSocketURL: string,
  mode: YjsProviderMode
): [WebsocketProvider, YJS.Doc] {
  const yDoc = new YJS.Doc();
  const isDocumentMode = mode === "document";
  const roomName = isDocumentMode ? `${mode}/${entityId}` : `${entityId}/${fieldSlug}`;
  const yjsProvider = new WebsocketProvider(webSocketURL || defaultWebSocketURL, roomName, yDoc, {
    params: isDocumentMode
      ? {
          accessToken,
          account: accountSlug,
          application: `${applicationId}`,
          report: `${entityId}`,
          env,
          mode,
        }
      : {
          accessToken,
          initialize: Boolean(!initialYjsData).toString(),
          account: accountSlug,
          application: `${applicationId}`,
          record: `${entityId}`,
          field: fieldSlug ?? "",
          env,
          mode,
        },
    connect: false,
  });

  yjsProvider.awareness.setLocalStateField("user", {
    name: memberName,
    color: baseColors[Math.floor(Math.random() * baseColors.length)],
  });

  return [yjsProvider, yDoc];
}
